/**
 * Table des competences/pouvoirs - meme esprit qu'itemDefs.js (une
 * table statique, resolveAbilityDef gere le repli si une competence
 * cote serveur n'est pas encore connue du client).
 *
 * `archetype` : specifique a un heros (cf. HERO_STATS_PROFILES) - jamais
 * utilisable par un autre archetype, meme trouvee/achetee.
 *
 * `unlockLevel` : si defini, debloquee AUTOMATIQUEMENT des que le joueur
 * (du bon archetype) atteint ce niveau - cf. MainScene.checkLevelUp.
 * Absent/null = ne se debloque jamais toute seule, doit etre obtenue
 * autrement (loot, achat en boutique - representee comme un objet
 * "parchemin" dans l'inventaire, cf. itemDefs.js/category 'abilityScroll').
 *
 * `effectType` : 'aoe' (degats en zone, IMPLEMENTE), 'pierce' (transperce
 * plusieurs ennemis alignes, a construire), 'debuff' (ralentit/affaiblit
 * temporairement un ennemi, a construire), 'buff' (ameliore
 * temporairement une stat du heros, a construire) - les 3 derniers
 * reutiliseront le systeme de statusEffects deja construit pour
 * saignement/brulure, etendu pour porter des modificateurs de stat en
 * plus des degats par tic.
 */
export const ABILITY_DEFS = {
  // base abilities
  fireball: {
    id: "fireball",
    name: "Boule de feu",
    archetypes: [],
    description:
      "Projectile qui explose en zone au contact d'un ennemi (18 degats). 15 mana.",
    manaCost: 15,
    cooldownMs: 3000,
    effectType: "projectileAoe", // voyage comme un projectile, explose au contact - distinct de 'aoe' (instantane, centre sur le heros)
    damage: 18, // degats a TOUS les ennemis dans le rayon de l'EXPLOSION, pas juste celui touche en premier
    damagePercent: 0.7,
    scalesFrom: "ranged",
    radius: 70, // rayon de l'explosion au point d'impact
    projectileSpeed: 260,
    maxDistance: 400, // disparait sans exploser s'il ne touche rien avant cette distance
    unlockLevel: 1,
    damageType: "fire",
    inflictsEffect: {
      type: "burn",
      damageType: "fire",
      kind: "dot",
      chance: 0.3,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      ticks: 3,
    },
  },
  whirlwind: {
    id: "whirlwind",
    name: "Tourbillon",
    archetypes: ["guerrier", "archer"],
    description:
      "Frappe tous les ennemis a portee de melee (14 degats). 20 stamina.",
    staminaCost: 20,
    cooldownMs: 4000,
    effectType: "aoe",
    damage: 14,
    damagePercent: 0.6,
    scalesFrom: "melee",
    radius: 55, // proche de playerMeleeRange - degats tout autour, contrairement au cone normal de l'attaque de base
    unlockLevel: 3,
  },

  haste: {
    id: "haste",
    name: "Hâte",
    archetypes: ["guerrier", "archer"], // utilisable par tous - restreins si tu veux la limiter
    description:
      "Augmente ta vitesse de déplacement pendant un court moment. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 8000,
    effectType: "selfBuff",
    statModifiers: { moveSpeedPercent: 0.6 },
    durationMs: 4000,
    unlockLevel: 3, // a debloquer via parchemin/loot pour l'instant
  },
  slow: {
    id: "slow",
    name: "Ralentissement",
    archetypes: ["mage", "archer"],
    description: "Ralentit tous les ennemis à portée de mêlée. 15 mana.",
    manaCost: 15,
    cooldownMs: 6000,
    effectType: "aoeDebuff",
    radius: 55,
    statModifiers: { moveSpeedPercent: -0.5 },
    durationMs: 3000,
    unlockLevel: 2,
  },
  piercingArrow: {
    id: "piercingArrow",
    name: "Flèche perforante",
    archetypes: ["archer", "voleur"],
    description:
      "Tire une flèche qui transperce plusieurs ennemis alignés (14 degats + 60% de tes degats d'attaque). 15 stamina.",
    staminaCost: 15,
    cooldownMs: 3500,
    effectType: "pierce",
    damage: 14,
    damagePercent: 0.6,
    scalesFrom: "ranged",
    projectileSpeed: 380,
    maxDistance: 450,
    maxPierceCount: 3, // touche au plus 3 ennemis avant de disparaitre - optionnel, absent = infini (transperce jusqu'a maxDistance)
    unlockLevel: 1,
  },
  flameWeapon: {
    id: "flameWeapon",
    name: "Lame enflammée",
    archetypes: ["guerrier"],
    description:
      "Le prochain coup porté inflige des dégâts supplémentaires (+10) et une forte chance de brûlure. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 5000,
    effectType: "weaponImbue",
    bonusDamage: 10,
    damagePercent: 0.6,
    scalesFrom: "melee",
    damageType: "fire",
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.8, // haute expres - c'est un coup DELIBEREMENT enchante, pas le taux passif habituel d'une arme
      damagePerTick: 3,
      tickIntervalMs: 1000,
      ticks: 3,
    },
    unlockLevel: 5,
  },
  flamingEdge: {
    id: "flamingEdge",
    name: "Tranchant flamboyant",
    archetypes: [],
    description:
      "Le prochain coup porté est enflammé (+8 dégâts, brûlure). 12 stamina.",
    staminaCost: 12,
    cooldownMs: 4000,
    effectType: "weaponImbue",
    bonusDamage: 8,
    damagePercent: 0.4,
    scalesFrom: "melee",
    damageType: "fire",
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.7,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      ticks: 3,
    },
  },
  glacialEdge: {
    id: "glacialEdge",
    name: "Tranchant glacial",
    archetypes: [],
    description:
      "Le prochain coup porté est glacé (+6 dégâts, ralentit la cible). 12 stamina.",
    staminaCost: 12,
    cooldownMs: 4000,
    effectType: "weaponImbue",
    bonusDamage: 6,
    damagePercent: 0.4,
    scalesFrom: "melee",
    damageType: "cold",
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      statModifiers: { moveSpeedPercent: -0.5 },
      durationMs: 2500,
      chance: 0.8,
    },
  },
  shockingEdge: {
    id: "shockingEdge",
    name: "Tranchant foudroyant",
    archetypes: [],
    description:
      "Le prochain coup porté est électrifié (+10 dégâts). 12 stamina.",
    staminaCost: 12,
    cooldownMs: 4000,
    effectType: "weaponImbue",
    bonusDamage: 10,
    damagePercent: 0.4,
    scalesFrom: "melee",
    damageType: "lightning",
  },
  venomEdge: {
    id: "venomEdge",
    name: "Tranchant empoisonné",
    archetypes: [],
    description:
      "Le prochain coup porté est empoisonné (+5 dégâts, poison sur la durée). 12 stamina.",
    staminaCost: 12,
    cooldownMs: 4000,
    effectType: "weaponImbue",
    bonusDamage: 5,
    damagePercent: 0.3,
    scalesFrom: "melee",
    damageType: "poison",
    inflictsEffect: {
      type: "acid",
      kind: "dot",
      chance: 0.8,
      damagePerTick: 4,
      tickIntervalMs: 1000,
      ticks: 3,
    },
  },
  holyEdge: {
    id: "holyEdge",
    name: "Tranchant sacré",
    archetypes: [],
    description: "Le prochain coup porté est béni (+9 dégâts). 12 stamina.",
    staminaCost: 12,
    cooldownMs: 4000,
    effectType: "weaponImbue",
    bonusDamage: 9,
    damagePercent: 0.45,
    scalesFrom: "melee",
    damageType: "holy",
  },
  shadowEdge: {
    id: "shadowEdge",
    name: "Tranchant ténébreux",
    archetypes: [],
    description:
      "Le prochain coup porté est corrompu par l'ombre (+9 dégâts). 12 stamina.",
    staminaCost: 12,
    cooldownMs: 4000,
    effectType: "weaponImbue",
    bonusDamage: 9,
    damagePercent: 0.45,
    scalesFrom: "melee",
    damageType: "shadow",
  },
  fireWall: {
    id: "fireWall",
    name: "Mur de flammes",
    archetypes: ["mage"],
    description:
      "Crée un mur de flammes qui inflige des dégâts sur la durée aux ennemis qui le traversent (20 dégâts). 20 mana.",
    manaCost: 20,
    cooldownMs: 8000,
    effectType: "aoe",
    damage: 20,
    damagePercent: 0.6,
    scalesFrom: "melee",
    damageType: "fire",
    radius: 80,
    durationMs: 5000,
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.8, // haute expres - c'est un coup DELIBEREMENT enchante, pas le taux passif habituel d'une arme
      damagePerTick: 3,
      tickIntervalMs: 1000,
      ticks: 3,
    },
    unlockLevel: 1,
  },
  flameThrower: {
    id: "flameThrower",
    name: "Jet de flammes",
    archetypes: ["mage"],
    description:
      "Un cône de flammes brûle tout ce qui se trouve devant toi (16 dégâts). 18 mana.",
    manaCost: 18,
    cooldownMs: 4000,
    effectType: "cone",
    damage: 16,
    damagePercent: 0.5,
    scalesFrom: "melee",
    damageType: "fire",
    angleDegrees: 60,
    distance: 90,
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.4,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      ticks: 3,
    },
    unlockLevel: 4,
  },
  meteor: {
    id: "meteor",
    name: "Météore",
    archetypes: ["mage"],
    description:
      "Invoque un météore qui s'écrase avec fracas sur la zone visée (35 dégâts). 35 mana.",
    manaCost: 35,
    cooldownMs: 10000,
    effectType: "projectileAoe",
    damage: 35,
    damagePercent: 1,
    scalesFrom: "ranged",
    damageType: "fire",
    radius: 90,
    projectileSpeed: 180, // volontairement lent - le "temps de chute" du meteore
    maxDistance: 450,
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.5,
      damagePerTick: 4,
      tickIntervalMs: 1000,
      ticks: 3,
    },
    unlockLevel: 8,
  },
  fireNova: {
    id: "fireNova",
    name: "Nova de feu",
    archetypes: ["mage"],
    description:
      "Libère une explosion de feu tout autour de toi (22 dégâts). 25 mana.",
    manaCost: 25,
    cooldownMs: 6000,
    effectType: "aoe",
    damage: 22,
    damagePercent: 0.6,
    scalesFrom: "melee",
    damageType: "fire",
    radius: 75,
    unlockLevel: 5,
  },

  scoutPulse: {
    id: "scoutPulse",
    name: "Pulsation d'exploration",
    archetypes: [],
    description:
      "Révèle une large zone du brouillard de guerre autour de toi. 1 stamina.",
    staminaCost: 1,
    cooldownMs: 10,
    effectType: "fogPulse",
    radius: 40, // en CASES (coordonnees de tuiles), pas en pixels - different des autres competences
    ignoresWalls: true,
    // disabledBiomes: ["maze1", "maze2"], // ids de biomeConfig.js - absent/vide = utilisable partout
    unlockLevel: null,
  },

  detectTraps: {
    id: "detectTraps",
    name: "Détection de pièges",
    archetypes: [],
    description: "Révèle les pièges cachés à portée de vue. 10 stamina.",
    effectType: "detectTrap",
    cooldownMs: 100,
    staminaCost: 1,
    unlockLevel: null,
    // radius absent -> utilise this.playerVisionRadius par defaut (cf.
    // performDetectTrapAbility) - ajoute radius: N ici si tu veux un
    // rayon fixe different de la vision du heros
  },
  detectSecrets: {
    id: "detectSecrets",
    name: "Détection des secrets",
    archetypes: [],
    description:
      "Révèle plus clairement les leviers et passages cachés à portée de vue. 12 stamina.",
    effectType: "detectSecret",
    cooldownMs: 100,
    staminaCost: 1,
    unlockLevel: null,
  },
  treasureSense: {
    id: "treasureSense",
    name: "Instinct du trésor",
    archetypes: ["voleur"],
    description:
      "Révèle une large zone du brouillard de guerre autour de toi, pratique pour repérer coffres et sorties. 5 stamina.",
    staminaCost: 5,
    cooldownMs: 12000,
    effectType: "fogPulse",
    radius: 25,
    ignoresWalls: false,
    unlockLevel: 3,
  },

  shieldBash: {
    id: "shieldBash",
    name: "Coup de bouclier",
    archetypes: ["guerrier"],
    description:
      "Fonce en ligne droite, percute et repousse les ennemis sur le passage. Nécessite un bouclier équipé. 20 stamina.",
    staminaCost: 20,
    cooldownMs: 6000,
    effectType: "shieldBash",
    requiresShield: true,
    damage: 16,
    damagePercent: 0.6,
    scalesFrom: "melee",
    dashDistance: 150, // en pixels
    dashSpeed: 500,
    knockbackDistance: 40,
    unlockLevel: null,
  },
  taunt: {
    id: "taunt",
    name: "Cri de défi",
    archetypes: ["guerrier"],
    description: "Attire l'attention de tous les ennemis proches. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 10000,
    effectType: "taunt",
    radius: 150, // en pixels, comme les autres AoE
    unlockLevel: null,
  },
  repel: {
    id: "repel",
    name: "Répulsion",
    archetypes: [],
    description: "Repousse tous les ennemis proches. 15 mana.",
    manaCost: 15,
    cooldownMs: 8000,
    effectType: "repel",
    radius: 100,
    knockbackDistance: 80,
    damage: 0, // optionnel - laisse a 0 si tu veux un pur effet de recul, sans degats
    unlockLevel: null,
  },
  stun: {
    id: "stun",
    name: "Étourdissement",
    archetypes: [],
    description:
      "Étourdit tous les ennemis proches, les empêchant d'agir un court moment. 20 mana.",
    manaCost: 20,
    cooldownMs: 10000,
    effectType: "aoeStun",
    radius: 90,
    durationMs: 2500,
    unlockLevel: null,
  },
  summonAlly: {
    id: "summonAlly",
    name: "Invocation",
    archetypes: ["mage"],
    description:
      "Invoque un allié temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    manaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "gazer1", // cle SPRITE_REGISTRY a utiliser pour le visuel - a remplacer par un sprite plus adapte si tu en as un
    durationMs: null, // null = dure jusqu'a la mort uniquement, jamais d'expiration
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "fire",
    resistances: { fire: 1 },
    unlockLevel: null,
  },
  summonAlly2: {
    id: "summonAlly2",
    name: "Invocation 2",
    archetypes: ["mage"],
    description:
      "Invoque un allié temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    manaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "gazer2", // cle SPRITE_REGISTRY a utiliser pour le visuel - a remplacer par un sprite plus adapte si tu en as un
    durationMs: null, // null = dure jusqu'a la mort uniquement, jamais d'expiration
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWolf: {
    id: "summonWolf",
    name: "Invocation : Loup",
    archetypes: ["voleur"],
    description: "Invoque un loup rapide mais fragile. 25 stamina.",
    staminaCost: 25,
    cooldownMs: 18000,
    effectType: "summon",
    summonType: "enemyDefault", // remplace par un sprite dedie si tu en as un
    durationMs: 15000,
    hp: 25,
    damage: 12,
    defense: 0,
    damageType: "physical",
  },

  summonIceElemental: {
    id: "summonIceElemental",
    name: "Invocation : Élémentaire de glace",
    archetypes: ["mage"],
    description:
      "Invoque un élémentaire robuste et résistant au froid. 35 mana.",
    manaCost: 35,
    cooldownMs: 25000,
    effectType: "summon",
    summonType: "enemyDefault",
    durationMs: 25000,
    hp: 60,
    damage: 6,
    defense: 3,
    damageType: "cold",
    resistances: { cold: 1, fire: -0.5 },
  },
  summonAngryBrownMushroom: {
    id: "summonAngryBrownMushroom",
    name: "Invocation : Champignon brun en colère",
    archetypes: [],
    description:
      "Invoque un champignon brun en colère temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "angryBrownMushroom", // cle SPRITE_REGISTRY a utiliser pour le visuel - a remplacer par un sprite plus adapte si tu en as un
    durationMs: null, // null = dure jusqu'a la mort uniquement, jamais d'expiration
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGnomeGreen: {
    id: "summonGnomeGreen",
    name: "Invocation : Gnome Vert",
    archetypes: [],
    description:
      "Invoque un gnome vert temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "gnomeGreen",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },

  summonAngryTrent: {
    id: "summonAngryTrent",
    name: "Invocation : Ronchêne",
    archetypes: [],
    description:
      "Invoque un ronchêne temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "angryTrent", // cle SPRITE_REGISTRY a utiliser pour le visuel - a remplacer par un sprite plus adapte si tu en as un
    attackType: "ranged",
    durationMs: null, // null = dure jusqu'a la mort uniquement, jamais d'expiration
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },

  summonKnifedBat: {
    id: "summonKnifedBat",
    name: "Invocation : Chauve-souris poignardée",
    archetypes: [],
    description:
      "Invoque une chauve-souris poignardée temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "knifedBat", // cle SPRITE_REGISTRY a utiliser pour le visuel - a remplacer par un sprite plus adapte si tu en as un
    durationMs: null, // null = dure jusqu'a la mort uniquement, jamais d'expiration
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonDeer1: {
    id: "summonDeer1",
    name: "Invocation : Cerf",
    archetypes: [],
    description:
      "Invoque un cerf temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "deer1", // cle SPRITE_REGISTRY a utiliser pour le visuel - a remplacer par un sprite plus adapte si tu en as un
    durationMs: null, // null = dure jusqu'a la mort uniquement, jamais d'expiration
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },

  summonWolfFamiliar: {
    id: "summonWolfFamiliar",
    name: "Familier",
    archetypes: ["mage"],
    description:
      "Invoque un familier qui te suit d'étage en étage, jusqu'à sa mort. Grandit avec ton niveau. 40 mana.",
    manaCost: 1,
    cooldownMs: 30000,
    effectType: "summon",
    summonType: "wolfFamiliar",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.6,
    defenseScale: 0.5,
    damageType: "physical",
    persistent: true,
    growthConfig: {
      maxLevel: 20, // niveau du joueur au-dela duquel le familier arrete de grandir
      minScaleMultiplier: 1, // taille normale au niveau 1
      maxScaleMultiplier: 1.8, // taille au niveau maxLevel (et au-dela)
    },
  },
  shortDash: {
    id: "shortDash",
    name: "Ruée",
    archetypes: [],
    description: "Téléportation courte dans la direction actuelle. 10 stamina.",
    staminaCost: 10,
    cooldownMs: 4000,
    effectType: "teleportDash",
    distance: 120,
  },
  randomTeleport: {
    id: "randomTeleport",
    name: "Téléportation aléatoire",
    archetypes: [],
    description: "Téléporte vers un point aléatoire de l'étage. 20 mana.",
    manaCost: 1,
    cooldownMs: 15000,
    effectType: "randomTeleport",
  },
  stealth: {
    id: "stealth",
    name: "Furtivité",
    archetypes: ["voleur"],
    description: "Devient indétectable un court moment. 5 stamina.",
    staminaCost: 5,
    cooldownMs: 8000,
    effectType: "stealth",
    durationMs: 5000,
  },
  lightningBolt: {
    id: "lightningBolt",
    name: "Éclair",
    archetypes: ["mage"],
    description:
      "Un éclair frappe la première cible touchée (18 dégâts). 14 mana.",
    manaCost: 14,
    cooldownMs: 2800,
    effectType: "pierce",
    damage: 18,
    damagePercent: 0.6,
    scalesFrom: "ranged",
    damageType: "lightning",
    projectileSpeed: 500,
    maxDistance: 420,
    maxPierceCount: 1,
    unlockLevel: 2,
  },
  chainLightningSpell: {
    id: "chainLightningSpell",
    name: "Chaîne d'éclairs",
    archetypes: ["mage"],
    description:
      "L'éclair saute d'ennemi en ennemi (14 dégâts par saut). 22 mana.",
    manaCost: 22,
    cooldownMs: 5000,
    effectType: "chainLightning",
    damage: 14,
    damagePercent: 0.5,
    scalesFrom: "ranged",
    damageType: "lightning",
    maxJumps: 4,
    jumpRange: 130,
    unlockLevel: 5,
  },
  targetedStrike: {
    id: "targetedStrike",
    name: "Frappe ciblée",
    archetypes: ["mage"],
    description:
      "Une décharge concentrée frappe un point précis avec une grande violence (30 dégâts). 28 mana.",
    manaCost: 28,
    cooldownMs: 7000,
    effectType: "projectileAoe",
    damage: 30,
    damagePercent: 0.8,
    scalesFrom: "ranged",
    damageType: "lightning",
    radius: 40,
    projectileSpeed: 450,
    maxDistance: 400,
    unlockLevel: 7,
  },
  staticStorm: {
    id: "staticStorm",
    name: "Tempête statique",
    archetypes: ["mage"],
    description:
      "Une tempête électrique persistante frappe les ennemis alentour (9 dégâts/tick). 32 mana.",
    manaCost: 32,
    cooldownMs: 13000,
    effectType: "zone",
    damagePerTick: 9,
    damagePercent: 0.25,
    scalesFrom: "ranged",
    damageType: "lightning",
    radius: 90,
    tickIntervalMs: 700,
    durationMs: 5000,
    color: 0xffff66,
    unlockLevel: 9,
  },
  thorns: {
    id: "thorns",
    name: "Épines",
    archetypes: ["mage", "archer"],
    description:
      "Des épines jaillissent du sol devant toi (15 dégâts). 12 mana.",
    manaCost: 12,
    cooldownMs: 3500,
    effectType: "cone",
    damage: 15,
    damagePercent: 0.5,
    scalesFrom: "melee",
    damageType: "poison",
    angleDegrees: 70,
    distance: 80,
    unlockLevel: 2,
  },
  ivyWhip: {
    id: "ivyWhip",
    name: "Fouet de lierre",
    archetypes: ["mage"],
    description:
      "Un lierre acéré fouette les ennemis proches (13 dégâts). 15 stamina.",
    staminaCost: 15,
    cooldownMs: 3000,
    effectType: "aoe",
    damage: 13,
    damagePercent: 0.5,
    scalesFrom: "melee",
    damageType: "poison",
    radius: 50,
    unlockLevel: 3,
  },
  spores: {
    id: "spores",
    name: "Spores",
    archetypes: ["mage"],
    description:
      "Libère un nuage de spores qui engourdissent les ennemis proches.",
    manaCost: 18,
    cooldownMs: 6000,
    effectType: "aoeDebuff",
    radius: 60,
    statModifiers: { moveSpeedPercent: -0.35 },
    durationMs: 4000,
    unlockLevel: 5,
  },
  venomousBite: {
    id: "venomousBite",
    name: "Morsure venimeuse",
    archetypes: ["voleur"],
    description:
      "Le prochain coup porté injecte un poison virulent. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 5000,
    effectType: "weaponImbue",
    bonusDamage: 6,
    damagePercent: 0.4,
    scalesFrom: "melee",
    damageType: "poison",
    inflictsEffect: {
      type: "acid",
      kind: "dot",
      chance: 0.85,
      damagePerTick: 4,
      tickIntervalMs: 1000,
      ticks: 4,
    },
    unlockLevel: 4,
  },
  stunningBlow: {
    id: "stunningBlow",
    name: "Coup étourdissant",
    archetypes: ["guerrier"],
    description:
      "Le prochain coup a une chance d'étourdir la cible. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 7000,
    effectType: "weaponImbue",
    bonusDamage: 4,
    damagePercent: 0.2,
    scalesFrom: "melee",
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      statModifiers: {},
      durationMs: 1200,
      chance: 0.35,
    },
  },
  rendingBlow: {
    id: "rendingBlow",
    name: "Coup déchirant",
    archetypes: ["guerrier", "voleur"],
    description:
      "Le prochain coup a une chance de faire saigner la cible. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 6000,
    effectType: "weaponImbue",
    bonusDamage: 4,
    damagePercent: 0.2,
    scalesFrom: "melee",
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 0.5,
      damagePerTick: 4,
      tickIntervalMs: 1000,
      ticks: 4,
    },
  },
  firePool: {
    id: "firePool",
    name: "Mare de feu",
    archetypes: ["mage"],
    description: "Zone de feu persistante. 25 mana.",
    manaCost: 1,
    cooldownMs: 12000,
    effectType: "zone",
    radius: 70,
    durationMs: 6000,
    tickIntervalMs: 800,
    damagePerTick: 5,
    damagePercent: 0.6,
    scalesFrom: "ranged",
    damageType: "fire",
    color: 0xff6600,
  },
  curse: {
    id: "curse",
    name: "Malédiction",
    archetypes: ["mage"],
    description: "Réduit les dégâts infligés par les ennemis proches. 20 mana.",
    manaCost: 1,
    cooldownMs: 12000,
    effectType: "aoeCurse",
    radius: 100,
    durationMs: 6000,
    damagePercent: -0.4,
  },
  vampiricStrike: {
    id: "vampiricStrike",
    name: "Frappe vampirique",
    archetypes: ["voleur"],
    description:
      "Le prochain coup soigne un % des dégâts infligés. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 8000,
    effectType: "weaponImbue",
    healPercent: 0.5,
  },
  healingAura: {
    id: "healingAura",
    name: "Soin de zone",
    archetypes: [],
    description:
      "Soigne le héros, ses invocations et les alliés escortés à proximité. 25 mana.",
    staminaCost: 1,
    cooldownMs: 120,
    effectType: "aoeHeal",
    radius: 120,
    healFlat: 15, // PV fixes rendus a chaque cible
    healPercent: 0.2, // + 20% des PV max de la cible
    unlockLevel: null,
  },
  riposte: {
    id: "riposte",
    name: "Riposte",
    archetypes: ["guerrier"],
    description:
      "Renvoie une partie des dégâts subis un court moment. 20 stamina.",
    staminaCost: 20,
    cooldownMs: 10000,
    effectType: "riposte",
    durationMs: 4000,
    reflectPercent: 0.5,
  },
  parry: {
    id: "parry",
    name: "Parade",
    archetypes: ["guerrier"],
    description:
      "Réduit fortement les dégâts subis un court instant. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 8000,
    effectType: "parry",
    durationMs: 1500,
    damageReduction: 0.8,
  },
  poisonTrap: {
    id: "poisonTrap",
    name: "Piège empoisonné",
    archetypes: ["voleur"],
    description: "Pose un piège qui empoisonne au contact. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 8000,
    effectType: "trap",
    damageType: "poison",
    inflictsEffect: {
      type: "poison",
      kind: "dot",
      chance: 1,
      damagePerTick: 3,
      damagePercent: 0.6,
      scalesFrom: "ranged",
      tickIntervalMs: 1000,
      ticks: 4,
    },
    triggerRadius: 20,
    expiresAfterMs: 30000,
  },
  fireTrap: {
    id: "fireTrap",
    name: "Piège incendiaire",
    archetypes: ["voleur"],
    description: "Pose un piège qui enflamme au contact. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 8000,
    effectType: "trap",
    damageType: "fire",
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 1,
      damagePerTick: 4,
      damagePercent: 0.6,
      scalesFrom: "ranged",
      tickIntervalMs: 1000,
      ticks: 3,
    },
    triggerRadius: 20,
    expiresAfterMs: 30000,
  },
  bleedTrap: {
    id: "bleedTrap",
    name: "Piège lacérant",
    archetypes: ["voleur"],
    description: "Pose un piège qui lacère au contact. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 8000,
    effectType: "trap",
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 3,
      damagePercent: 0.6,
      scalesFrom: "ranged",
      tickIntervalMs: 800,
      ticks: 5,
    },
    triggerRadius: 20,
    expiresAfterMs: 30000,
  },
  freeze: {
    id: "freeze",
    name: "Gel",
    archetypes: ["mage"],
    description: "Projectile qui explose et gèle les ennemis touchés. 18 mana.",
    manaCost: 1,
    cooldownMs: 10, //5000
    effectType: "projectileAoe",
    damage: 10,
    damagePercent: 0.6,
    scalesFrom: "melee",
    damageType: "cold",
    radius: 60,
    projectileSpeed: 260,
    maxDistance: 400,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: {},
      durationMs: 1500,
    },
  },
  frozenWall: {
    id: "frozenWall",
    name: "Mur de givre",
    archetypes: ["mage"],
    description:
      "Vague de froid en cône qui ralentit sur son passage. 25 mana.",
    manaCost: 1,
    cooldownMs: 10000,
    effectType: "cone",
    distance: 180,
    angleDegrees: 60,
    damage: 8,
    damagePercent: 0.6,
    scalesFrom: "ranged",
    damageType: "cold",
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      chance: 1,
      statModifiers: { moveSpeedPercent: -0.5 },
      durationMs: 3000,
    },
  },
  iceBolt: {
    id: "iceBolt",
    name: "Projectile de glace",
    archetypes: ["mage"],
    description:
      "Un projectile glacé qui ralentit sa cible (12 dégâts). 10 mana.",
    manaCost: 10,
    cooldownMs: 2500,
    effectType: "pierce",
    damage: 12,
    damagePercent: 0.5,
    scalesFrom: "ranged",
    damageType: "cold",
    projectileSpeed: 340,
    maxDistance: 400,
    maxPierceCount: 1,
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      statModifiers: { moveSpeedPercent: -0.4 },
      durationMs: 2500,
      chance: 0.7,
    },
    unlockLevel: 1,
  },
  freezingRay: {
    id: "freezingRay",
    name: "Rayon gelant",
    archetypes: ["mage"],
    description:
      "Un cône de froid glace tout ce qui se trouve devant toi (14 dégâts). 20 mana.",
    manaCost: 20,
    cooldownMs: 5000,
    effectType: "cone",
    damage: 14,
    damagePercent: 0.5,
    scalesFrom: "melee",
    damageType: "cold",
    angleDegrees: 50,
    distance: 100,
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      statModifiers: { moveSpeedPercent: -0.6 },
      durationMs: 3000,
      chance: 0.9,
    },
    unlockLevel: 4,
  },
  frostNova: {
    id: "frostNova",
    name: "Nova de givre",
    archetypes: ["mage"],
    description:
      "Une explosion de givre ralentit tous les ennemis proches. 20 mana.",
    manaCost: 20,
    cooldownMs: 6000,
    effectType: "aoeDebuff",
    radius: 70,
    statModifiers: { moveSpeedPercent: -0.5 },
    durationMs: 3000,
    unlockLevel: 3,
  },
  icePrison: {
    id: "icePrison",
    name: "Prison de glace",
    archetypes: ["mage"],
    description: "Enracine tous les ennemis proches dans la glace. 25 mana.",
    manaCost: 25,
    cooldownMs: 9000,
    effectType: "aoeRoot",
    radius: 65,
    durationMs: 2500,
    unlockLevel: 6,
  },
  blizzard: {
    id: "blizzard",
    name: "Blizzard",
    archetypes: ["mage"],
    description:
      "Crée une tempête de glace persistante qui blesse les ennemis qui la traversent (10 dégâts/tick). 30 mana.",
    manaCost: 30,
    cooldownMs: 12000,
    effectType: "zone",
    damagePerTick: 10,
    damagePercent: 0.3,
    scalesFrom: "melee",
    damageType: "cold",
    radius: 80,
    tickIntervalMs: 800,
    durationMs: 6000,
    color: 0x99ddff,
    unlockLevel: 9,
  },
  root: {
    id: "root",
    name: "Racines",
    archetypes: ["mage"],
    description:
      "Bloque le mouvement des ennemis proches (ils peuvent encore attaquer). 18 mana.",
    manaCost: 1,
    cooldownMs: 9000,
    effectType: "aoeRoot",
    radius: 90,
    durationMs: 3000,
  },
  stoneThrow: {
    id: "stoneThrow",
    name: "Projection de pierre",
    archetypes: ["mage", "archer"],
    description: "Projette un bloc de pierre sur ta cible (16 dégâts). 8 mana.",
    manaCost: 8,
    cooldownMs: 2200,
    effectType: "pierce",
    damage: 16,
    damagePercent: 0.5,
    scalesFrom: "ranged",
    damageType: "physical",
    projectileSpeed: 300,
    maxDistance: 380,
    maxPierceCount: 1,
    unlockLevel: 1,
  },
  localizedQuake: {
    id: "localizedQuake",
    name: "Séisme localisé",
    archetypes: ["mage", "guerrier"],
    description:
      "Fait trembler le sol, étourdissant les ennemis proches (18 dégâts). 24 mana.",
    manaCost: 24,
    cooldownMs: 8000,
    effectType: "aoeStun",
    damage: 18,
    radius: 65,
    durationMs: 1500,
    unlockLevel: 6,
  },
  earthLance: {
    id: "earthLance",
    name: "Lance de terre",
    archetypes: ["mage"],
    description:
      "Une lance de pierre transperce les ennemis alignés (20 dégâts). 18 mana.",
    manaCost: 18,
    cooldownMs: 4500,
    effectType: "pierce",
    damage: 20,
    damagePercent: 0.6,
    scalesFrom: "ranged",
    damageType: "physical",
    projectileSpeed: 320,
    maxDistance: 400,
    maxPierceCount: 3,
    unlockLevel: 5,
  },
  magicMissile: {
    id: "magicMissile",
    name: "Projectile magique",
    archetypes: ["mage"],
    description:
      "Un projectile d'énergie arcanique basique, fiable et peu coûteux (10 dégâts). 6 mana.",
    manaCost: 6,
    cooldownMs: 1800,
    effectType: "pierce",
    damage: 10,
    damagePercent: 0.45,
    scalesFrom: "ranged",
    damageType: "arcane",
    projectileSpeed: 360,
    maxDistance: 380,
    maxPierceCount: 1,
    unlockLevel: 1,
  },
  energyRay: {
    id: "energyRay",
    name: "Rayon d'énergie",
    archetypes: ["mage"],
    description:
      "Un rayon d'énergie pure explose au contact (20 dégâts). 20 mana.",
    manaCost: 20,
    cooldownMs: 4500,
    effectType: "projectileAoe",
    damage: 20,
    damagePercent: 0.65,
    scalesFrom: "ranged",
    damageType: "arcane",
    radius: 55,
    projectileSpeed: 340,
    maxDistance: 400,
    unlockLevel: 4,
  },
  arcaneExplosion: {
    id: "arcaneExplosion",
    name: "Explosion arcanique",
    archetypes: ["mage"],
    description:
      "Libère une violente explosion d'énergie arcanique autour de toi (26 dégâts). 30 mana.",
    manaCost: 30,
    cooldownMs: 7000,
    effectType: "aoe",
    damage: 26,
    damagePercent: 0.7,
    scalesFrom: "melee",
    damageType: "arcane",
    radius: 70,
    unlockLevel: 7,
  },
  boomerangAxe: {
    id: "boomerangAxe",
    name: "Hache boomerang",
    archetypes: ["guerrier"],
    description: "Lance une hache qui revient après son trajet. 18 stamina.",
    staminaCost: 18,
    cooldownMs: 6000,
    effectType: "boomerang",
    damage: 12,
    damagePercent: 0.6,
    scalesFrom: "ranged",
    projectileSpeed: 300,
    maxDistance: 250,
  },
  vortex: {
    id: "vortex",
    name: "Vortex",
    archetypes: ["mage"],
    description: "Attire tous les ennemis proches vers toi. 20 mana.",
    manaCost: 1,
    cooldownMs: 10000,
    effectType: "vortex",
    radius: 150,
    pullDistance: 80,
  },
  execute: {
    id: "execute",
    name: "Exécution",
    archetypes: [],
    description:
      "Le prochain coup inflige des dégâts massifs si la cible est sous 25% de PV. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 8000,
    effectType: "weaponImbue",
    executeThreshold: 0.25,
    executeBonusMultiplier: 3,
  },
  extendedVision: {
    id: "extendedVision",
    name: "Vision perçante",
    archetypes: [],
    description: "Augmente ta portée de vue un moment. 15 mana.",
    manaCost: 1,
    cooldownMs: 15000,
    effectType: "visionBuff",
    visionBonus: 4,
    durationMs: 10000,
  },
  bloodPact: {
    id: "bloodPact",
    name: "Pacte de sang",
    archetypes: ["mage"],
    description: "Convertit des PV en mana instantanément.",
    cooldownMs: 15000,
    effectType: "bloodPact",
    hpCost: 20,
    resourceGain: 30,
    resourceType: "mana",
  },
  berserker: {
    id: "berserker",
    name: "Rage berserker",
    archetypes: ["guerrier"],
    description:
      "Sous 30% de PV : +80% dégâts, -30% défense un moment. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 15000,
    effectType: "conditionalBuff",
    hpThresholdPercent: 0.3,
    durationMs: 6000,
    buffStatModifiers: {
      meleeDamagePercent: 0.8,
      rangedDamagePercent: 0.8,
      defensePercent: -0.3,
    },
  },
  recklessRage: {
    id: "recklessRage",
    name: "Rage à double tranchant",
    archetypes: ["guerrier"],
    description: "+50% dégâts, -25% défense, sans condition. 15 stamina.",
    staminaCost: 15,
    cooldownMs: 10000,
    effectType: "selfBuff",
    durationMs: 6000,
    buffStatModifiers: {
      meleeDamagePercent: 0.5,
      rangedDamagePercent: 0.5,
      defensePercent: -0.25,
    },
  },
  shadowRay: {
    id: "shadowRay",
    name: "Rayon des ténèbres",
    archetypes: ["mage"],
    description:
      "Un rayon d'ombre corrosif transperce ta cible (17 dégâts). 16 mana.",
    manaCost: 16,
    cooldownMs: 3500,
    effectType: "pierce",
    damage: 17,
    damagePercent: 0.55,
    scalesFrom: "ranged",
    damageType: "shadow",
    projectileSpeed: 320,
    maxDistance: 400,
    maxPierceCount: 1,
    unlockLevel: 3,
  },
  curseOfWeakness: {
    id: "curseOfWeakness",
    name: "Malédiction d'affaiblissement",
    archetypes: ["mage"],
    description:
      "Maudit les ennemis proches, augmentant fortement les dégâts qu'ils subissent. 22 mana.",
    manaCost: 22,
    cooldownMs: 9000,
    effectType: "aoeCurse",
    radius: 70,
    damagePercent: 0.4,
    durationMs: 5000,
    unlockLevel: 5,
  },

  holyRay: {
    id: "holyRay",
    name: "Rayon sacré",
    archetypes: ["mage", "guerrier"],
    description:
      "Un rayon de lumière sacrée transperce ta cible (16 dégâts). 15 mana.",
    manaCost: 15,
    cooldownMs: 3200,
    effectType: "pierce",
    damage: 16,
    damagePercent: 0.5,
    scalesFrom: "ranged",
    damageType: "holy",
    projectileSpeed: 340,
    maxDistance: 400,
    maxPierceCount: 1,
    unlockLevel: 3,
  },
  smite: {
    id: "smite",
    name: "Châtiment",
    archetypes: ["mage", "guerrier"],
    description: "Frappe la zone d'une lumière punitive (24 dégâts). 26 mana.",
    manaCost: 26,
    cooldownMs: 7000,
    effectType: "aoe",
    damage: 24,
    damagePercent: 0.65,
    scalesFrom: "melee",
    damageType: "holy",
    radius: 65,
    unlockLevel: 6,
  },
  spectralBlade: {
    id: "spectralBlade",
    name: "Lame spectrale",
    archetypes: ["guerrier"],
    description:
      "Une lame d'énergie transperce les ennemis alignés (19 dégâts). 18 stamina.",
    staminaCost: 18,
    cooldownMs: 4000,
    effectType: "pierce",
    damage: 19,
    damagePercent: 0.6,
    scalesFrom: "melee",
    damageType: "physical",
    projectileSpeed: 380,
    maxDistance: 350,
    maxPierceCount: 2,
    unlockLevel: 4,
  },
  forceProjectile: {
    id: "forceProjectile",
    name: "Projectile de force",
    archetypes: ["guerrier", "archer"],
    description:
      "Concentre ta force en un projectile brutal (18 dégâts). 16 stamina.",
    staminaCost: 16,
    cooldownMs: 3500,
    effectType: "pierce",
    damage: 18,
    damagePercent: 0.55,
    scalesFrom: "melee",
    damageType: "physical",
    projectileSpeed: 350,
    maxDistance: 350,
    maxPierceCount: 1,
    unlockLevel: 3,
  },
  shockwave: {
    id: "shockwave",
    name: "Onde de choc",
    archetypes: ["guerrier"],
    description:
      "Une onde de choc repousse et blesse tous les ennemis proches (15 dégâts). 22 stamina.",
    staminaCost: 22,
    cooldownMs: 6000,
    effectType: "repel",
    damage: 15,
    damagePercent: 0.5,
    scalesFrom: "melee",
    damageType: "physical",
    radius: 60,
    knockbackDistance: 60,
    unlockLevel: 5,
  },
  bruteForce: {
    id: "bruteForce",
    name: "Force brute",
    archetypes: [],
    description:
      "Augmente fortement tes dégâts au corps à corps pendant un court moment. 20 stamina.",
    staminaCost: 20,
    cooldownMs: 10000,
    effectType: "selfBuff",
    statModifiers: { meleeDamagePercent: 0.5 },
    durationMs: 5000,
    unlockLevel: 4,
  },
  precision: {
    id: "precision",
    name: "Précision",
    archetypes: [],
    description:
      "Augmente fortement tes dégâts à distance pendant un court moment. 20 stamina.",
    staminaCost: 20,
    cooldownMs: 10000,
    effectType: "selfBuff",
    statModifiers: { rangedDamagePercent: 0.5 },
    durationMs: 5000,
    unlockLevel: 4,
  },
  preciseStrike: {
    id: "preciseStrike",
    name: "Frappe précise",
    archetypes: [],
    description:
      "Augmente fortement tes chances de coup critique sur le prochain coup. 12 stamina.",
    staminaCost: 12,
    cooldownMs: 6000,
    effectType: "weaponImbue",
    critChanceBonus: 0.35, // s'ajoute a CRIT_CHANCE, pour CE coup uniquement
  },
  manaShield: {
    id: "manaShield",
    name: "Armure de mana",
    archetypes: ["mage"],
    description:
      "Renforce ta défense en puisant dans ton énergie magique pendant un court moment. 15 mana.",
    manaCost: 15,
    cooldownMs: 8000,
    effectType: "selfBuff",
    statModifiers: { defensePercent: 0.6 },
    durationMs: 6000,
    unlockLevel: 4,
  },
  summonMudGolem: {
    id: "summonMudGolem",
    name: "Invocation : Golem de boue",
    archetypes: [],
    description:
      "Invoque un golem de boue temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "mudGolem",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRedBeetle: {
    id: "summonRedBeetle",
    name: "Invocation : Insecte Rouge",
    archetypes: [],
    description:
      "Invoque un insecte rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "redBeetle",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPinkOgre: {
    id: "summonPinkOgre",
    name: "Invocation : Ogre rose",
    archetypes: [],
    description:
      "Invoque un ogre rose temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "pinkOgre",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBat: {
    id: "summonBat",
    name: "Invocation : Chauve-souris",
    archetypes: [],
    description:
      "Invoque une chauve-souris temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "bat",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBrownBear: {
    id: "summonBrownBear",
    name: "Invocation : Ours brun",
    archetypes: [],
    description:
      "Invoque un ours brun temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "brownBear",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBee: {
    id: "summonBee",
    name: "Invocation : Abeille",
    archetypes: [],
    description:
      "Invoque une abeille temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "bee",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBigtick: {
    id: "summonBigtick",
    name: "Invocation : Gros tique",
    archetypes: [],
    description:
      "Invoque un gros tique temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "bigtick",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlackdragon: {
    id: "summonBlackdragon",
    name: "Invocation : Dragon noir",
    archetypes: [],
    description:
      "Invoque un dragon noir temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blackdragon",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonDarkelf: {
    id: "summonDarkelf",
    name: "Invocation : Elfe noir",
    archetypes: [],
    description:
      "Invoque un elfe noir temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "darkelf",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonDemonDragon: {
    id: "summonDemonDragon",
    name: "Invocation : Dragon démon",
    archetypes: [],
    description:
      "Invoque un dragon démon temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "demonDragon",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonDwarf: {
    id: "summonDwarf",
    name: "Invocation : Nain",
    archetypes: [],
    description:
      "Invoque un nain temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "dwarf",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonFantasy1: {
    id: "summonFantasy1",
    name: "Invocation : Fantasy 1",
    archetypes: [],
    description:
      "Invoque un fantasy 1 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "fantasy1",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonFantasy2: {
    id: "summonFantasy2",
    name: "Invocation : Fantasy 2",
    archetypes: [],
    description:
      "Invoque un fantasy 2 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "fantasy2",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonFantasy3: {
    id: "summonFantasy3",
    name: "Invocation : Fantasy 3",
    archetypes: [],
    description:
      "Invoque un fantasy 3 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "fantasy3",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonFantasy4: {
    id: "summonFantasy4",
    name: "Invocation : Fantasy 4",
    archetypes: [],
    description:
      "Invoque un fantasy 4 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "fantasy4",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrqueGreen: {
    id: "summonOrqueGreen",
    name: "Invocation : Orque Verte",
    archetypes: [],
    description:
      "Invoque une orque verte temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orqueGreen",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonredWarriorMushroom: {
    id: "summonredWarriorMushroom",
    name: "Invocation : Guerrier Champignon Rouge",
    archetypes: [],
    description:
      "Invoque un guerrier champignon rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "redWarriorMushroom",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonFantasy7: {
    id: "summonFantasy7",
    name: "Invocation : Fantasy 7",
    archetypes: [],
    description:
      "Invoque un fantasy 7 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "fantasy7",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonMassecailleBlue: {
    id: "summonMassecailleBlue",
    name: "Invocation : Massecaille Bleu",
    archetypes: [],
    description:
      "Invoque un Massecaille Bleu temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "massecailleBlue",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonFantasy9: {
    id: "summonFantasy9",
    name: "Invocation : Fantasy 9",
    archetypes: [],
    description:
      "Invoque un fantasy 9 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "fantasy9",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonKnightJauneRouge: {
    id: "summonKnightJauneRouge",
    name: "Invocation : Chevalier Jaune Rouge",
    archetypes: [],
    description:
      "Invoque un chevalier jaune rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "knightJauneRouge",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGargoyle: {
    id: "summonGargoyle",
    name: "Invocation : Gargouille",
    archetypes: [],
    description:
      "Invoque une gargouille temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "gargoyle",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGhost: {
    id: "summonGhost",
    name: "Invocation : Fantome",
    archetypes: [],
    description:
      "Invoque un fantome temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "ghost",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGnomeFou: {
    id: "summonGnomeFou",
    name: "Invocation : Gnome fou",
    archetypes: [],
    description:
      "Invoque un gnome fou temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "gnomeFou",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGolem: {
    id: "summonGolem",
    name: "Invocation : Golem",
    archetypes: [],
    description:
      "Invoque un golem temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "golem",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGorilla: {
    id: "summonGorilla",
    name: "Invocation : Gorille",
    archetypes: [],
    description:
      "Invoque un gorille temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "gorilla",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGreendragon: {
    id: "summonGreendragon",
    name: "Invocation : Dragon vert",
    archetypes: [],
    description:
      "Invoque un dragon vert temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "greendragon",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOgreBrown: {
    id: "summonOgreBrown",
    name: "Invocation : Gourdogre — Clan Fangebois",
    archetypes: [],
    description:
      "Invoque un gourdogre du Clan Fangebois qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "ogreBrown",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRedbeetle: {
    id: "summonRedbeetle",
    name: "Invocation : Insecte rouge",
    archetypes: [],
    description:
      "Invoque un insecte rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "redbeetle",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot1: {
    id: "summonRobot1",
    name: "Invocation : Robot1",
    archetypes: [],
    description:
      "Invoque un robot1 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot1",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot2: {
    id: "summonRobot2",
    name: "Invocation : Robot2",
    archetypes: [],
    description:
      "Invoque un robot2 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot2",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot3: {
    id: "summonRobot3",
    name: "Invocation : Robot3",
    archetypes: [],
    description:
      "Invoque un robot3 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot3",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot4: {
    id: "summonRobot4",
    name: "Invocation : Robot4",
    archetypes: [],
    description:
      "Invoque un robot4 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot4",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot5: {
    id: "summonRobot5",
    name: "Invocation : Robot5",
    archetypes: [],
    description:
      "Invoque un robot5 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot5",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot6: {
    id: "summonRobot6",
    name: "Invocation : Robot6",
    archetypes: [],
    description:
      "Invoque un robot6 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot6",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot7: {
    id: "summonRobot7",
    name: "Invocation : Robot7",
    archetypes: [],
    description:
      "Invoque un robot7 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot7",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot8: {
    id: "summonRobot8",
    name: "Invocation : Robot8",
    archetypes: [],
    description:
      "Invoque un robot8 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot8",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot9: {
    id: "summonRobot9",
    name: "Invocation : Robot9",
    archetypes: [],
    description:
      "Invoque un robot9 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot9",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRobot10: {
    id: "summonRobot10",
    name: "Invocation : Robot10",
    archetypes: [],
    description:
      "Invoque un robot10 temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "robot10",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonSkeleton: {
    id: "summonSkeleton",
    name: "Invocation : Squelette",
    archetypes: [],
    description:
      "Invoque un squelette temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "skeleton",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonSkeletonking: {
    id: "summonSkeletonking",
    name: "Invocation : Roi Squelette",
    archetypes: [],
    description:
      "Invoque un roi squelette temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "skeletonking",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGreenSlime: {
    id: "summonGreenSlime",
    name: "Invocation : Blob vert",
    archetypes: [],
    description:
      "Invoque un blob vert temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "greenSlime",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonSpider: {
    id: "summonSpider",
    name: "Invocation : Tarentule",
    archetypes: [],
    description:
      "Invoque une tarentule temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "spider",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonYellowSlime: {
    id: "summonYellowSlime",
    name: "Invocation : Blob jaune",
    archetypes: [],
    description:
      "Invoque un blob jaune temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "yellowSlime",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlueSlime: {
    id: "summonBlueSlime",
    name: "Invocation : Blob bleu",
    archetypes: [],
    description:
      "Invoque un blob bleu temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blueSlime",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPurpleSlime: {
    id: "summonPurpleSlime",
    name: "Invocation : Blob violet",
    archetypes: [],
    description:
      "Invoque un blob violet temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "purpleSlime",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonYellowWarriorMushroom: {
    id: "summonYellowWarriorMushroom",
    name: "Invocation : Guerrier Champignon Jaune",
    archetypes: [],
    description:
      "Invoque un guerrier champignon jaune temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "yellowWarriorMushroom",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlueWarriorMushroom: {
    id: "summonBlueWarriorMushroom",
    name: "Invocation : Guerrier Champignon Bleu",
    archetypes: [],
    description:
      "Invoque un guerrier chamignon bleu temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blueWarriorMushroom",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGreenWarriorMushroom: {
    id: "summonGreenWarriorMushroom",
    name: "Invocation : Guerrier Champignon Vert",
    archetypes: [],
    description:
      "Invoque un guerrier champignon vert temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "greenWarriorMushroom",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPurpleWarriorMushroom: {
    id: "summonPurpleWarriorMushroom",
    name: "Invocation : Guerrier Champignon Violet",
    archetypes: [],
    description:
      "Invoque un guerrier champignon violet temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "purpleWarriorMushroom",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlackBear: {
    id: "summonBlackBear",
    name: "Invocation : Ours noir",
    archetypes: [],
    description:
      "Invoque un ours noir temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blackBear",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWhiteBear: {
    id: "summonWhiteBear",
    name: "Invocation : Ours Blanc",
    archetypes: [],
    description:
      "Invoque un ours blanc temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "whiteBear",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonKnightBleuArgent: {
    id: "summonKnightBleuArgent",
    name: "Invocation : Chevalier Bleu Argent",
    archetypes: [],
    description:
      "Invoque un chevalier bleu argent temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "knightBleuArgent",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.5,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonKnightNoirCramoisi: {
    id: "summonKnightNoirCramoisi",
    name: "Invocation : Chevalier Noir Cramoisi",
    archetypes: [],
    description:
      "Invoque un chevalier noir cramoisi temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "knightNoirCramoisi",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonKnightVertOr: {
    id: "summonKnightVertOr",
    name: "Invocation : Chevalier Vert or",
    archetypes: [],
    description:
      "Invoque un chevalier vert or temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "knightVertOr",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonKnightVioletArgent: {
    id: "summonKnightVioletArgent",
    name: "Invocation : Chevalier Violet Argent",
    archetypes: [],
    description:
      "Invoque un chevalier violet argent temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "knightVioletArgent",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonMassecaillePurple: {
    id: "summonMassecaillePurple",
    name: "Invocation : Massecaille Violet",
    archetypes: [],
    description:
      "Invoque un massecaille violet temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "massecaillePurple",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonMassecailleGreen: {
    id: "summonMassecailleGreen",
    name: "Invocation : Massecaille Vert",
    archetypes: [],
    description:
      "Invoque un massecaille vert temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "massecailleGreen",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonMassecailleRed: {
    id: "summonMassecailleRed",
    name: "Invocation : Massecaille Rouge",
    archetypes: [],
    description:
      "Invoque un massecaille rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "massecailleRed",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonMassecailleYellow: {
    id: "summonMassecailleYellow",
    name: "Invocation : Massecaille Jaune",
    archetypes: [],
    description:
      "Invoque un massecaille jaune temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "massecailleYellow",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrqueBlack: {
    id: "summonOrqueBlack",
    name: "Invocation : Orque en noir",
    archetypes: [],
    description:
      "Invoque une orque en noir temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orqueBlack",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrqueYellow: {
    id: "summonOrqueYellow",
    name: "Invocation : Orque en jaune",
    archetypes: [],
    description:
      "Invoque une orque en jaune temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orqueYellow",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrqueBlue: {
    id: "summonOrqueBlue",
    name: "Invocation : Orque en bleu",
    archetypes: [],
    description:
      "Invoque une orque en bleu temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orqueBlue",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrqueRed: {
    id: "summonOrqueRed",
    name: "Invocation : Orque en rouge",
    archetypes: [],
    description:
      "Invoque une orque en rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orqueRed",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrquePurple: {
    id: "summonOrquePurple",
    name: "Invocation : Orque en violet",
    archetypes: [],
    description:
      "Invoque une orque en violet temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orquePurple",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrqueGrey: {
    id: "summonOrqueGrey",
    name: "Invocation : Orque en gris",
    archetypes: [],
    description:
      "Invoque une orque en gris temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orqueGrey",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonTrollBlue: {
    id: "summonTrollBlue",
    name: "Invocation : Troll Bleu",
    archetypes: [],
    description:
      "Invoque un troll bleu temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "trollBlue",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonTrollGris: {
    id: "summonTrollGris",
    name: "Invocation : Troll Gris",
    archetypes: [],
    description:
      "Invoque un troll gris temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "trollGris",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonTrollRose: {
    id: "summonTrollRose",
    name: "Invocation : Troll rose",
    archetypes: [],
    description:
      "Invoque une troll rose temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "trollRose",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonTrollRouge: {
    id: "summonTrollRouge",
    name: "Invocation : Troll Rouge",
    archetypes: [],
    description:
      "Invoque un troll rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "trollRouge",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonTrollVert: {
    id: "summonTrollVert",
    name: "Invocation : Troll vert",
    archetypes: [],
    description:
      "Invoque un troll vert temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "trollVert",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonTrollViolet: {
    id: "summonTrollViolet",
    name: "Invocation : Troll Violet",
    archetypes: [],
    description:
      "Invoque une troll  violette temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "trollViolet",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWarlockRed: {
    id: "summonWarlockRed",
    name: "Invocation : Sorcier Rouge",
    archetypes: [],
    description:
      "Invoque un sorcier rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "warlockRed",
    attackType: "ranged",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWarlockWhite: {
    id: "summonWarlockWhite",
    name: "Invocation : Sorcier Blanc",
    archetypes: [],
    description:
      "Invoque un sorcier blanc temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "warlockWhite",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWarlockGreen: {
    id: "summonWarlockGreen",
    name: "Invocation : Sorcier Vert",
    archetypes: [],
    description:
      "Invoque un sorcier vert temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "warlockGreen",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWarlockBlue: {
    id: "summonWarlockBlue",
    name: "Invocation : Sorcier Bleu",
    archetypes: [],
    description:
      "Invoque un sorcier bleu temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "warlockBlue",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWarlockBlack: {
    id: "summonWarlockBlack",
    name: "Invocation : Sorcier Noir",
    archetypes: [],
    description:
      "Invoque un sorcier noir temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "warlockBlack",
    attackType: "ranged",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWarlockPurple: {
    id: "summonWarlockPurple",
    name: "Invocation : Sorcier Violet",
    archetypes: [],
    description:
      "Invoque un sorcier violet temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "warlockPurple",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGolemEau: {
    id: "summonGolemEau",
    name: "Invocation : Golem d'eau",
    archetypes: [],
    description:
      "Invoque un golem d'eau temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "golemEau",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 1.5,
    defenseScale: 0.5,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGolemFeu: {
    id: "summonGolemFeu",
    name: "Invocation : Golem de feu",
    archetypes: [],
    description:
      "Invoque un golem de feu temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "golemFeu",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGolemFoudre: {
    id: "summonGolemFoudre",
    name: "Invocation : Golem de foudre",
    archetypes: [],
    description:
      "Invoque un golem de foudre temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "golemFoudre",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGolemGlace: {
    id: "summonGolemGlace",
    name: "Invocation : Golem de glace",
    archetypes: [],
    description:
      "Invoque un golem de glace temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "golemGlace",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGolemOmbre: {
    id: "summonGolemOmbre",
    name: "Invocation : Golem d'ombre",
    archetypes: [],
    description:
      "Invoque un golem d'ombre temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "golemOmbre",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonZombiequeenBoss: {
    id: "summonZombiequeenBoss",
    name: "Invocation : Reine Zombie",
    archetypes: [],
    description:
      "Invoque une reine zombie temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "zombiequeen",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.9,
    defenseScale: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonZombiequeen: {
    id: "summonZombiequeen",
    name: "Invocation : Reine Zombie",
    archetypes: [],
    description:
      "Invoque une reine zombie temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "zombiequeen",
    durationMs: null,
    hp: 40,
    damage: 8,
    defense: 1,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBeequeenBoss: {
    id: "summonBeequeenBoss",
    name: "Invocation : Reine des abeilles",
    archetypes: [],
    description:
      "Invoque une reine des abeilles temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "beequeen",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBeequeen: {
    id: "summonBeequeen",
    name: "Invocation : Abeille Royale",
    archetypes: [],
    description:
      "Invoque une abeille royale temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "beequeen",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGrayGriblin: {
    id: "summonGrayGriblin",
    name: "Invocation : Griblin",
    archetypes: [],
    description:
      "Invoque un griblin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "grayGriblin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGreenGriblin: {
    id: "summonGreenGriblin",
    name: "Invocation : Griblin Emeraude",
    archetypes: [],
    description:
      "Invoque un griblin émeraude temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "greenGriblin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRedGriblin: {
    id: "summonRedGriblin",
    name: "Invocation : Griblin Ecarlate",
    archetypes: [],
    description:
      "Invoque un griblin écarlate temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "redGriblin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlueGriblin: {
    id: "summonBlueGriblin",
    name: "Invocation : Grinblin Azur",
    archetypes: [],
    description:
      "Invoque un griblin azur temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blueGriblin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPurpleGriblin: {
    id: "summonPurpleGriblin",
    name: "Invocation : Griblin Améthyste",
    archetypes: [],
    description:
      "Invoque un griblin améthyste temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "purpleGriblin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGoldGriblin: {
    id: "summonGoldGriblin",
    name: "Invocation : Griblin Doré",
    archetypes: [],
    description:
      "Invoque un griblin doré temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "goldGriblin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRedManditaure: {
    id: "summonRedManditaure",
    name: "Invocation : Manditaure écarlate",
    archetypes: [],
    description:
      "Invoque un manditaure écarlate temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "redManditaure",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlueManditaure: {
    id: "summonBlueManditaure",
    name: "Invocation : Manditaure azur",
    archetypes: [],
    description:
      "Invoque un manditaure azur temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blueManditaure",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGreenManditaure: {
    id: "summonGreenManditaure",
    name: "Invocation : Manditaure émeraude",
    archetypes: [],
    description:
      "Invoque un manditaure émeraude temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "greenManditaure",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPurpleManditaure: {
    id: "summonPurpleManditaure",
    name: "Invocation : Manditaure améthyste",
    archetypes: [],
    description:
      "Invoque une manditaure améthyste temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "purpleManditaure",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonYellowManditaure: {
    id: "summonYellowManditaure",
    name: "Invocation : Manditaure doré",
    archetypes: [],
    description:
      "Invoque un manditaure doré temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "yellowManditaure",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlackManditaure: {
    id: "summonBlackManditaure",
    name: "Invocation : Manditaure noir",
    archetypes: [],
    description:
      "Invoque un manditaure noir temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blackManditaure",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPurpleSpectrelin: {
    id: "summonPurpleSpectrelin",
    name: "Invocation : Spectrelin",
    archetypes: [],
    description:
      "Invoque un spectrelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "purpleSpectrelin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrangeSpectrelin: {
    id: "summonOrangeSpectrelin",
    name: "Invocation : Brumelin",
    archetypes: [],
    description:
      "Invoque un brumelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orangeSpectrelin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCyanSpectrelin: {
    id: "summonCyanSpectrelin",
    name: "Invocation : Glaselin",
    archetypes: [],
    description:
      "Invoque un glaselin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "cyanSpectrelin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPinkSpectrelin: {
    id: "summonPinkSpectrelin",
    name: "Invocation : Hanteline",
    archetypes: [],
    description:
      "Invoque une hanteline temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "pinkSpectrelin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonSilverSpectrelin: {
    id: "summonSilverSpectrelin",
    name: "Invocation : Ombrelin",
    archetypes: [],
    description:
      "Invoque un ombrelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "silverSpectrelin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBronzeSpectrelin: {
    id: "summonBronzeSpectrelin",
    name: "Invocation : Echolin",
    archetypes: [],
    description:
      "Invoque un écholin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "bronzeSpectrelin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGreenGemmacrabe: {
    id: "summonGreenGemmacrabe",
    name: "Invocation : Gemmacrabe",
    archetypes: [],
    description:
      "Invoque un gemmacrabe temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "greenGemmacrabe",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRedGemmacrabe: {
    id: "summonRedGemmacrabe",
    name: "Invocation : Braisecrabe",
    archetypes: [],
    description:
      "Invoque un braisecrabe temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "redGemmacrabe",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlueGemmacrabe: {
    id: "summonBlueGemmacrabe",
    name: "Invocation : Abyssacrabe",
    archetypes: [],
    description:
      "Invoque un abyssacrabe temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blueGemmacrabe",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonYellowGemmacrabe: {
    id: "summonYellowGemmacrabe",
    name: "Invocation : Ambracrabre",
    archetypes: [],
    description:
      "Invoque un ambracrabe temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "yellowGemmacrabe",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrangeGemmacrabe: {
    id: "summonOrangeGemmacrabe",
    name: "Invocation : Corailcrabe",
    archetypes: [],
    description:
      "Invoque un corailcrabe temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orangeGemmacrabe",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWhiteGemmacrabe: {
    id: "summonWhiteGemmacrabe",
    name: "Invocation : Nacrabe",
    archetypes: [],
    description:
      "Invoque un nacrabe temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "whiteGemmacrabe",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonBlueGrayVesperin: {
    id: "summonBlueGrayVesperin",
    name: "Invocation : Vesperin",
    archetypes: [],
    description:
      "Invoque un vesperin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "blueGrayVesperin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonSilverVesperin: {
    id: "summonSilverVesperin",
    name: "Invocation : Astrelin",
    archetypes: [],
    description:
      "Invoque un astrelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "silverVesperin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGoldVesperin: {
    id: "summonGoldVesperin",
    name: "Invocation : Solelin",
    archetypes: [],
    description:
      "Invoque un solelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "goldVesperin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCopperVesperin: {
    id: "summonCopperVesperin",
    name: "Invocation : Forgelin",
    archetypes: [],
    description:
      "Invoque un forgelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "copperVesperin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonSteelVesperin: {
    id: "summonSteelVesperin",
    name: "Invocation : Lamelin",
    archetypes: [],
    description:
      "Invoque un lamelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "steelVesperin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonEmeraldVesperin: {
    id: "summonEmeraldVesperin",
    name: "Invocation : Jadelin",
    archetypes: [],
    description:
      "Invoque un jadelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "emeraldVesperin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrangeTikorin: {
    id: "summonOrangeTikorin",
    name: "Invocation : Tikorin",
    archetypes: [],
    description:
      "Invoque un tikorin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orangeTikorin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonLightOakTikorin: {
    id: "summonLightOakTikorin",
    name: "Invocation : Clarorin",
    archetypes: [],
    description:
      "Invoque un clarorin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "lightOakTikorin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonWalnutTikorin: {
    id: "summonWalnutTikorin",
    name: "Invocation : Racorin",
    archetypes: [],
    description:
      "Invoque un racorin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "walnutTikorin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonMahoganyTikorin: {
    id: "summonMahoganyTikorin",
    name: "Invocation : Braisorin",
    archetypes: [],
    description:
      "Invoque un braisorin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "mahoganyTikorin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonEbonyTikorin: {
    id: "summonEbonyTikorin",
    name: "Invocation : Noctorin",
    archetypes: [],
    description:
      "Invoque un noctorin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "ebonyTikorin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonGoldenBeechTikorin: {
    id: "summonGoldenBeechTikorin",
    name: "Invocation : Auréorin",
    archetypes: [],
    description:
      "Invoque un auréorin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "goldenBeechTikorin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPinkLimacorne: {
    id: "summonPinkLimacorne",
    name: "Invocation : Limacorne",
    archetypes: [],
    description:
      "Invoque une limacorne temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "pinkLimacorne",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonDeepBlueLimacorne: {
    id: "summonDeepBlueLimacorne",
    name: "Invocation : Gouffrorne",
    archetypes: [],
    description:
      "Invoque une gouffrorne temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "deepBlueLimacorne",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonTurquoiseLimacorne: {
    id: "summonTurquoiseLimacorne",
    name: "Invocation : Réciforne",
    archetypes: [],
    description:
      "Invoque une réciforne temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "turquoiseLimacorne",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonAlgaeGreenLimacorne: {
    id: "summonAlgaeGreenLimacorne",
    name: "Invocation : Algorne",
    archetypes: [],
    description:
      "Invoque une algorne temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "algaeGreenLimacorne",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCoralLimacorne: {
    id: "summonCoralLimacorne",
    name: "Invocation : Corallorne",
    archetypes: [],
    description:
      "Invoque une corallorne temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "coralLimacorne",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonPearlLimacorne: {
    id: "summonPearlLimacorne",
    name: "Invocation : Perlorne",
    archetypes: [],
    description:
      "Invoque une perlorne temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "pearlLimacorne",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCraquelin: {
    id: "summonCraquelin",
    name: "Invocation : Craquelin",
    archetypes: [],
    description:
      "Invoque un craquelin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "craquelin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCraqueglace: {
    id: "summonCraqueglace",
    name: "Invocation : Craqueglace",
    archetypes: [],
    description:
      "Invoque un craqueglace temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "craqueglace",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCraquevenin: {
    id: "summonCraquevenin",
    name: "Invocation : Craquevenin",
    archetypes: [],
    description:
      "Invoque un craquevenin temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "craquevenin",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCraquarcane: {
    id: "summonCraquarcane",
    name: "Invocation : Craquarcane",
    archetypes: [],
    description:
      "Invoque un craquarcane temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "craquarcane",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCraquor: {
    id: "summonCraquor",
    name: "Invocation : Craquor",
    archetypes: [],
    description:
      "Invoque un craquor temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "craquor",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonCraquombre: {
    id: "summonCraquombre",
    name: "Invocation : Craquombre",
    archetypes: [],
    description:
      "Invoque un craquombre temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "craquombre",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonRedSlime: {
    id: "summonRedSlime",
    name: "Invocation : Blob rouge",
    archetypes: [],
    description:
      "Invoque un blob rouge temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "redSlime",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonOrangeSlime: {
    id: "summonOrangeSlime",
    name: "Invocation : Blob orange",
    archetypes: [],
    description:
      "Invoque un blob orange temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "orangeSlime",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
  summonSkyBlueSlime: {
    id: "summonSkyBlueSlime",
    name: "Invocation : Blob bleu ciel",
    archetypes: [],
    description:
      "Invoque un blob bleu ciel temporaire qui combat à tes côtés, jusqu'à sa mort ou expiration. 30 mana.",
    staminaCost: 1,
    cooldownMs: 15000,
    effectType: "summon",
    summonType: "skyBlueSlime",
    durationMs: null,
    hpScale: 0.5,
    damageScale: 0.5,
    defenseScale: 0.8,
    damageType: "physical",
    resistances: { physical: 1 },
    unlockLevel: null,
  },
};

/**
 * Resout la definition d'une competence par son id - repli generique si
 * elle n'est pas (encore) connue du client, meme principe que
 * resolveItemDef.
 */
export function resolveAbilityDef(abilityId) {
  return (
    ABILITY_DEFS[abilityId] || {
      id: abilityId,
      name: abilityId,
      archetypes: [],
      staminaCost: 10,
      cooldownMs: 2000,
      effectType: "aoe",
      damage: 5,
      radius: 50,
      unlockLevel: null,
    }
  );
}
